import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { M365User } from "../types";

interface Group {
  id: string;
  display_name?: string;
  mail?: string;
  security_enabled?: boolean;
}
interface Member {
  id: string;
  display_name?: string;
  upn?: string;
}

/**
 * Entra ID group management. Scoped by the global customer switcher (no local
 * picker). Browse the tenant's groups, expand to view members, add/remove
 * members. Backed by /api/v1/m365/{customer_id}/groups[/{id}/members]. The
 * add-member picker is sourced from that tenant's synced M365 users.
 */
export default function M365GroupsPanel({
  users,
  customerId,
}: {
  users: M365User[];
  customerId: number | null;
}) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [addSel, setAddSel] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState<"m365" | "security">("m365");
  const [creating, setCreating] = useState(false);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const loadGroups = useCallback(() => {
    setOpenGroup(null);
    setMembers([]);
    if (customerId == null) {
      setGroups([]);
      return;
    }
    setLoading(true);
    setError("");
    api
      .get(`/api/v1/m365/${customerId}/groups`)
      .then((r) => setGroups(r.data))
      .catch((e: unknown) => {
        setGroups([]);
        const err = e as { response?: { data?: { detail?: string } } };
        setError(
          err.response?.data?.detail ||
            "Couldn't load groups (is M365 configured for this customer?)",
        );
      })
      .finally(() => setLoading(false));
  }, [customerId]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const refreshMembers = (gid: string) =>
    api
      .get(`/api/v1/m365/${customerId}/groups/${gid}/members`)
      .then((r) => setMembers(r.data))
      .catch((e: unknown) => {
        const err = e as { response?: { data?: { detail?: string } } };
        setError(err.response?.data?.detail || "Couldn't load members");
      });

  const openMembers = (gid: string) => {
    setOpenGroup(gid);
    setMembers([]);
    setAddSel("");
    setError("");
    refreshMembers(gid);
  };

  const addMember = async (gid: string) => {
    if (!addSel) return;
    setError("");
    try {
      await api.post(`/api/v1/m365/${customerId}/groups/${gid}/members`, {
        member_object_id: addSel,
      });
      setAddSel("");
      refreshMembers(gid);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Add member failed");
    }
  };

  const removeMember = async (gid: string, oid: string) => {
    setError("");
    try {
      await api.delete(`/api/v1/m365/${customerId}/groups/${gid}/members/${oid}`);
      refreshMembers(gid);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Remove member failed");
    }
  };

  const createGroup = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError("");
    try {
      await api.post(`/api/v1/m365/${customerId}/groups`, {
        display_name: newName.trim(),
        group_type: newType,
      });
      setNewName("");
      loadGroups();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Create group failed");
    } finally {
      setCreating(false);
    }
  };

  const deleteGroup = async (gid: string, name?: string) => {
    if (!window.confirm(`Delete group "${name || gid}"? This can't be undone.`)) return;
    setError("");
    try {
      await api.delete(`/api/v1/m365/${customerId}/groups/${gid}`);
      if (openGroup === gid) setOpenGroup(null);
      loadGroups();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Delete group failed");
    }
  };

  const tenantUsers = users
    .filter((u) => u.customer_id === customerId)
    .sort((a, b) => (a.display_name || a.upn || "").localeCompare(b.display_name || b.upn || ""));
  const memberIds = new Set(members.map((m) => m.id));
  const sortedGroups = [...groups].sort((a, b) => {
    const av = (a.display_name || a.id).toLowerCase();
    const bv = (b.display_name || b.id).toLowerCase();
    return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  if (customerId == null) {
    return (
      <p className="text-sm text-gray-400 dark:text-gray-500 italic">
        Select a customer in the header to manage its Entra groups.
      </p>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New group name"
          className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm w-56"
        />
        <select
          value={newType}
          onChange={(e) => setNewType(e.target.value as "m365" | "security")}
          className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
        >
          <option value="m365">Microsoft 365 group</option>
          <option value="security">Security group</option>
        </select>
        <button
          onClick={createGroup}
          disabled={creating || !newName.trim()}
          className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {creating ? "Creating…" : "+ Create group"}
        </button>
      </div>

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg mb-3">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Loading groups…</p>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm divide-y divide-gray-100 dark:divide-gray-700">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <button
              onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase inline-flex items-center gap-1 hover:text-gray-800 dark:hover:text-gray-100"
              title="Sort by name"
            >
              Name <span className="text-gray-400 dark:text-gray-500">{sortDir === "asc" ? "▲" : "▼"}</span>
            </button>
            <span className="text-xs text-gray-400 dark:text-gray-500">{groups.length}</span>
          </div>
          {sortedGroups.length === 0 && (
            <p className="px-4 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
              No groups found.
            </p>
          )}
          {sortedGroups.map((g) => (
            <div key={g.id}>
              <div
                className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                title={`${g.display_name || g.id}${g.mail ? "\n" + g.mail : ""}${g.security_enabled ? "\nSecurity-enabled" : ""}`}
              >
                <button
                  onClick={() => (openGroup === g.id ? setOpenGroup(null) : openMembers(g.id))}
                  className="flex-1 flex items-center gap-2 text-left"
                >
                  <span className="text-sm text-gray-800 dark:text-gray-100">
                    {g.display_name || g.id}
                    {g.mail && <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">{g.mail}</span>}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">{openGroup === g.id ? "▲" : "▼"}</span>
                </button>
                <button
                  onClick={() => deleteGroup(g.id, g.display_name)}
                  className="text-xs text-red-600 dark:text-red-400 hover:underline ml-3"
                >
                  Delete
                </button>
              </div>
              {openGroup === g.id && (
                <div className="px-4 pb-3 bg-gray-50/50 dark:bg-gray-900/40">
                  <div className="space-y-1 mb-2">
                    {members.length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-gray-500 italic py-1">No members.</p>
                    ) : (
                      members.map((m) => (
                        <div
                          key={m.id}
                          className="flex items-center justify-between text-sm py-0.5"
                        >
                          <span className="text-gray-700 dark:text-gray-200">
                            {m.display_name || m.upn || m.id}
                            {m.upn && m.display_name && (
                              <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">{m.upn}</span>
                            )}
                          </span>
                          <button
                            onClick={() => removeMember(g.id, m.id)}
                            className="text-xs text-red-600 dark:text-red-400 hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={addSel}
                      onChange={(e) => setAddSel(e.target.value)}
                      className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-xs bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Add a member…</option>
                      {tenantUsers
                        .filter((u) => !memberIds.has(u.object_id))
                        .map((u) => (
                          <option key={u.object_id} value={u.object_id}>
                            {u.display_name || u.upn}
                          </option>
                        ))}
                    </select>
                    <button
                      onClick={() => addMember(g.id)}
                      disabled={!addSel}
                      className="text-xs bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
